import React, { useState, useMemo } from "react";
import { trackSearch, trackSort, trackRosterView, trackFilter } from "../utils/analytics";
import { prefetchProfile } from "../api";
import NotifyButton from "./NotifyButton";
import VenueInfo from "./VenueInfo";
import ViewModeToggle from "./ViewModeToggle";
import RosterGallery from "./RosterGallery";
import useViewMode, { VIEW_GALLERY } from "../utils/viewMode";

// Split a "HH:MMam-HH:MMpm" style time string into start/finish.
function parseTimeRange(timeStr) {
  if (!timeStr) return { start: "", finish: "" };
  const match = timeStr.match(
    /(\d{1,2}([:.]\d{2})? ?[ap]m)[^\d]*(\d{1,2}([:.]\d{2})? ?[ap]m)/i
  );
  if (match) {
    return {
      start: match[1].replace(/\s+/g, ""),
      finish: match[3].replace(/\s+/g, ""),
    };
  }
  return { start: timeStr, finish: "" };
}

const titleCase = (str) =>
  (str || "")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

// The scraper now emits `code` and a clean `name` on every entry, so the
// frontend simply reads those fields. The `else` branch is a defensive fallback
// for any legacy cached data (old object without `code`, or a bare string) and
// can be removed once all cached results have been regenerated by a scrape.
export function parseNameTime(entry) {
  if (entry && typeof entry === "object" && ("code" in entry || "name" in entry)) {
    const { start, finish } = parseTimeRange(entry.time);
    return {
      code: entry.code || "",
      name: entry.name || "",
      start,
      finish,
    };
  }

  // Legacy fallback: a plain "Code Name Time" string with no separate code field.
  const raw = typeof entry === "string" ? entry : (entry && entry.name) || "";
  let rest = raw;
  let code = "";
  const codeMatch = rest.match(/^([A-Za-z]{1,5})\s+/);
  if (codeMatch) {
    code = codeMatch[1];
    rest = rest.replace(codeMatch[0], "");
  }
  const { start, finish } = parseTimeRange(rest);
  const name = titleCase(rest.replace(/(\d{1,2}([:.]\d{2})? ?[ap]m)[^\d]*(\d{1,2}([:.]\d{2})? ?[ap]m)/i, "").trim());
  return { code, name, start, finish };
}

function RosterTable({ entries, profileVenue }) {
  const [filter, setFilter] = useState("");
  const [sortField, setSortField] = useState("start");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isNsfw, setIsNsfw] = useState(() => {
    const saved = localStorage.getItem('nsfw_mode');
    if (saved === 'false') return false;
    if (saved === 'true') return true;
    return document.body.classList.contains('nsfw-mode');
  });

  // Stored list/gallery preference. Gallery is photo-only, so SFW mode always falls
  // back to the list even when gallery is the saved choice - the preference is kept,
  // not overwritten, so it returns when NSFW is switched back on.
  const viewMode = useViewMode();
  const showGallery = isNsfw && viewMode === VIEW_GALLERY;

  // Keep link mode in sync with global NSFW toggle state.
  React.useEffect(() => {
    const syncNsfw = () => {
      const saved = localStorage.getItem('nsfw_mode');
      const next = saved === 'false' ? false : true;
      setIsNsfw(next);
    };

    const onNsfwChanged = (event) => {
      setIsNsfw(Boolean(event.detail));
    };

    syncNsfw();
    window.addEventListener('nsfw-mode-changed', onNsfwChanged);
    return () => window.removeEventListener('nsfw-mode-changed', onNsfwChanged);
  }, []);

  // Parse all entries first
  const parsedEntries = useMemo(() => {
    if (!entries || entries.length === 0) return [];
    return entries.map((entry, idx) => ({
      ...parseNameTime(entry),
      originalIndex: idx,
      originalEntry: entry
    }));
  }, [entries]);

  // Filter entries based on search term
  const filteredEntries = useMemo(() => {
    if (!filter.trim()) return parsedEntries;
    
    const searchTerm = filter.toLowerCase().trim();
    return parsedEntries.filter(entry => 
      entry.code.toLowerCase().includes(searchTerm) ||
      entry.name.toLowerCase().includes(searchTerm) ||
      entry.start.toLowerCase().includes(searchTerm) ||
      entry.finish.toLowerCase().includes(searchTerm)
    );
  }, [parsedEntries, filter]);

  // Sort entries
  const sortedEntries = useMemo(() => {
    const sorted = [...filteredEntries];
    
    // If sortField is "original", preserve the original backend order
    if (sortField === "original") {
      sorted.sort((a, b) => {
        return sortDirection === "asc" ? 
          a.originalIndex - b.originalIndex : 
          b.originalIndex - a.originalIndex;
      });
      return sorted;
    }
    
    sorted.sort((a, b) => {
      let aValue = a[sortField] || "";
      let bValue = b[sortField] || "";
      
      // Special handling for time sorting
      if (sortField === "start" || sortField === "finish") {
        // Convert time to 24-hour format for proper sorting
        const timeToMinutes = (timeStr) => {
          if (!timeStr) return 0;
          const match = timeStr.match(/(\d{1,2})(?:[:.:](\d{2}))?\s*([ap])m/i);
          if (!match) return 0;
          
          let hours = parseInt(match[1]);
          const minutes = parseInt(match[2] || "0");
          const period = match[3].toLowerCase();
          
          if (period === 'p' && hours !== 12) hours += 12;
          if (period === 'a' && hours === 12) hours = 0;
          
          return hours * 60 + minutes;
        };
        
        aValue = timeToMinutes(aValue);
        bValue = timeToMinutes(bValue);
      }
      
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [filteredEntries, sortField, sortDirection]);

  const handleSort = (field) => {
    // Track sort action
    trackSort(field);
    
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "⇅";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const clearFilter = () => {
    setFilter("");
    trackFilter("clear", "");
  };

  const resetToOriginalOrder = () => {
    setSortField("original");
    setSortDirection("asc");
    trackSort("original_order");
  };

  // Track search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilter(value);
    
    // Simple debounce - only track after user stops typing for 500ms
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      if (value.trim()) {
        trackSearch(value.trim());
      }
    }, 500);
  };

  return (
    <div>
      {/* Filter Controls */}
      <div className="row mb-3">
        <div className="col-md-8">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, code, or time..."
              value={filter}
              onChange={handleSearchChange}
            />
            <button 
              className="btn btn-outline-secondary" 
              type="button" 
              onClick={clearFilter}
              disabled={!filter.trim()}
            >
              Clear
            </button>
            <button 
              className="btn btn-outline-primary" 
              type="button" 
              onClick={resetToOriginalOrder}
              disabled={sortField === "original"}
              title="Reset to original backend order"
            >
              Original Order
            </button>
          </div>
        </div>
        <div className="col-md-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <small className="text-muted">
            Showing {sortedEntries.length} of {parsedEntries.length} entries
            {sortField === "original" && " (original order)"}
            {sortField !== "original" && ` (sorted by ${sortField})`}
          </small>
          {/* Gallery is all photographs, so the switch only appears in NSFW mode. */}
          {isNsfw && <ViewModeToggle />}
        </div>
      </div>

      {showGallery ? (
        <RosterGallery entries={sortedEntries} venue={profileVenue} />
      ) : (
      <>
      {/* Table. The wrapper scrolls horizontally as a safety net: without it a
          narrow viewport clips the last column (Notify) off-screen entirely
          rather than making it reachable. Mobile CSS tightens the columns so
          scrolling shouldn't normally be needed. */}
      <div className="roster-table-wrap">
      <table className="table roster-table">
        <thead>
          <tr>
            <th
              className={sortField === "code" ? "sortable sort-active" : "sortable"}
              onClick={() => handleSort("code")}
            >
              Code {getSortIcon("code")}
            </th>
            <th
              className={sortField === "name" ? "sortable sort-active" : "sortable"}
              onClick={() => handleSort("name")}
            >
              Name {getSortIcon("name")}
            </th>
            <th
              className={sortField === "start" ? "sortable sort-active" : "sortable"}
              onClick={() => handleSort("start")}
            >
              Start {getSortIcon("start")}
            </th>
            <th
              className={sortField === "finish" ? "sortable sort-active" : "sortable"}
              onClick={() => handleSort("finish")}
            >
              Finish {getSortIcon("finish")}
            </th>
            <th className="notify-col">Notify</th>
          </tr>
        </thead>
        <tbody>
          {sortedEntries.length > 0 ? (
            sortedEntries.map((entry, idx) => (
              <tr key={entry.originalIndex} style={{ "--row-index": idx }}>
                <td>{entry.code ? <span className="code-tag">{entry.code}</span> : null}</td>
                <td className="name-cell">
                  {isNsfw ? (
                    <a
                      href="#"
                      onMouseEnter={() => prefetchProfile(entry.name, profileVenue)}
                      onFocus={() => prefetchProfile(entry.name, profileVenue)}
                      onTouchStart={() => prefetchProfile(entry.name, profileVenue)}
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.hash = `#profile-${profileVenue}-${entry.name.replace(/[\s-]+$/, '')}`;
                      }}
                    >
                      {entry.name}
                    </a>
                  ) : (
                    entry.name
                  )}
                </td>
                <td>{entry.start ? <span className="time-chip">{entry.start}</span> : null}</td>
                <td>{entry.finish ? <span className="time-chip">{entry.finish}</span> : null}</td>
                <td className="notify-col">
                  {entry.name ? <NotifyButton venue={profileVenue} performerName={entry.name} /> : null}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="text-center">
                {filter.trim() ? "No entries match your search" : "No data"}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      </>
      )}

      {/* Disclaimer */}
      <div className="mt-2">
        <small className="text-muted fst-italic">
          ⚠️ This may not be 100% accurate and may have added or missing persons. 
          Please go to the original website to verify the real roster.
        </small>
      </div>
    </div>
  );
}

function No5Roster({ site, lastUpdated }) {
  const [tab, setTab] = useState("today");
  const { today, tomorrow, title } = site;
  
  const handleTabChange = (newTab) => {
    setTab(newTab);
    trackRosterView(`${title}-${newTab}`);
  };
  
  // Determine the date string from lastUpdated
  let dateStr = lastUpdated ? new Date(lastUpdated).toLocaleDateString() : "";
  let tomorrowDateStr = lastUpdated ? new Date(Date.parse(lastUpdated) + 24*60*60*1000).toLocaleDateString() : "";
  return (
    <div className="roster-data" style={{ marginBottom: 40 }}>
      <h2 className="venue-title">{title}</h2>
      <div className="day-toggle" style={{ marginBottom: 16, marginTop: 12 }}>
        <button
          onClick={() => handleTabChange("today")}
          className={`btn btn-sm ${
            tab === "today" ? "btn-primary" : "btn-outline-primary"
          }`}
          style={{ marginRight: 8 }}
        >
          Today's Roster{dateStr && ` (${dateStr})`}
        </button>
        <button
          onClick={() => handleTabChange("tomorrow")}
          className={`btn btn-sm ${
            tab === "tomorrow" ? "btn-primary" : "btn-outline-primary"
          }`}
        >
          Tomorrow's Roster{tomorrowDateStr && ` (${tomorrowDateStr})`}
        </button>
      </div>
      {tab === "tomorrow" && (!tomorrow || tomorrow.length === 0) ? (
        <div className="alert alert-info" role="alert">
          <strong>Roster not yet available for tomorrow.</strong> Please check back later. This is usually released after 6 PM.
        </div>
      ) : tab === "tomorrow" && tomorrow.length === 1 && tomorrow[0].name && `${tomorrow[0].code || ""} ${tomorrow[0].name || ""}`.toLowerCase().includes("roster will be available") ? (
        <div className="alert alert-info" role="alert">
          <strong>{`${tomorrow[0].code || ""} ${tomorrow[0].name || ""}`.trim()}</strong>
        </div>
      ) : (
        <RosterTable entries={tab === "today" ? today : tomorrow} profileVenue="marrickville" />
      )}
    </div>
  );
}

function NightshadeRoster({ site, lastUpdated }) {
  const [tab, setTab] = useState("today");
  const { today, tomorrow, title } = site;
  
  const handleTabChange = (newTab) => {
    setTab(newTab);
    trackRosterView(`${title}-${newTab}`);
  };
  
  // Determine the date string from lastUpdated
  let dateStr = lastUpdated ? new Date(lastUpdated).toLocaleDateString() : "";
  let tomorrowDateStr = lastUpdated ? new Date(Date.parse(lastUpdated) + 24*60*60*1000).toLocaleDateString() : "";
  
  return (
    <div className="roster-data" style={{ marginBottom: 40 }}>
      <h2 className="venue-title">{title}</h2>
      <div className="day-toggle" style={{ marginBottom: 16, marginTop: 12 }}>
        <button
          onClick={() => handleTabChange("today")}
          className={`btn btn-sm ${
            tab === "today" ? "btn-primary" : "btn-outline-primary"
          }`}
          style={{ marginRight: 8 }}
        >
          Today's Roster{dateStr && ` (${dateStr})`}
        </button>
        <button
          onClick={() => handleTabChange("tomorrow")}
          className={`btn btn-sm ${
            tab === "tomorrow" ? "btn-primary" : "btn-outline-primary"
          }`}
        >
          Tomorrow's Roster{tomorrowDateStr && ` (${tomorrowDateStr})`}
        </button>
      </div>
      {tab === "tomorrow" && (!tomorrow || tomorrow.length === 0) ? (
        <div className="alert alert-info" role="alert">
          <strong>Roster not yet available for tomorrow.</strong> Please check back later. This is usually released after 6 PM.
        </div>
      ) : tab === "tomorrow" && tomorrow.length === 1 && tomorrow[0].name && `${tomorrow[0].code || ""} ${tomorrow[0].name || ""}`.toLowerCase().includes("roster will be available") ? (
        <div className="alert alert-info" role="alert">
          <strong>{`${tomorrow[0].code || ""} ${tomorrow[0].name || ""}`.trim()}</strong>
        </div>
      ) : (
        <RosterTable entries={tab === "today" ? today : tomorrow} profileVenue="gladesville" />
      )}
    </div>
  );
}

function GinzaRoster({ site, lastUpdated }) {
  const { rosters, title } = site;
  const [tab, setTab] = useState("today");
  
  const handleTabChange = (newTab) => {
    setTab(newTab);
    trackRosterView(`${title}-${newTab}`);
  };
  
  const todayBlock = rosters && rosters.length > 0 ? rosters[0] : null;
  const tomorrowBlock = rosters && rosters.length > 1 ? rosters[1] : null;
  let dateStr = lastUpdated ? new Date(lastUpdated).toLocaleDateString() : "";
  let tomorrowDateStr = lastUpdated ? new Date(Date.parse(lastUpdated) + 24*60*60*1000).toLocaleDateString() : "";
  return (
    <div className="roster-data" style={{ marginBottom: 40 }}>
      <h2 className="venue-title">{title}</h2>
      <div className="day-toggle" style={{ marginBottom: 16, marginTop: 12 }}>
        <button
          onClick={() => handleTabChange("today")}
          className={`btn btn-sm ${
            tab === "today" ? "btn-primary" : "btn-outline-primary"
          }`}
          style={{ marginRight: 8 }}
        >
          Today's Roster{dateStr && ` (${dateStr})`}
        </button>
        <button
          onClick={() => handleTabChange("tomorrow")}
          className={`btn btn-sm ${
            tab === "tomorrow" ? "btn-primary" : "btn-outline-primary"
          }`}
        >
          Tomorrow's Roster{tomorrowDateStr && ` (${tomorrowDateStr})`}
        </button>
      </div>
      {tab === "tomorrow" && !tomorrowBlock ? (
        <div className="alert alert-info" role="alert">
          <strong>Roster not yet available for tomorrow.</strong> Please check back later. This is usually released after 6 PM.
        </div>
      ) : tab === "tomorrow" && tomorrowBlock && tomorrowBlock.names && tomorrowBlock.names.length === 0 ? (
        <div className="alert alert-info" role="alert">
          <strong>Roster not yet available for tomorrow.</strong> Please check back later. This is usually released after 6 PM.
        </div>
      ) : (
        <RosterTable entries={tab === "today" ? (todayBlock ? todayBlock.names : []) : (tomorrowBlock ? tomorrowBlock.names : [])} profileVenue="cleveland" />
      )}
    </div>
  );
}

function Ginza479Roster({ site, lastUpdated }) {
  const { rosters, title } = site;
  const [tab, setTab] = useState("today");
  const todayBlock = rosters && rosters.length > 0 ? rosters[0] : null;
  const tomorrowBlock = rosters && rosters.length > 1 ? rosters[1] : null;
  let dateStr = lastUpdated ? new Date(lastUpdated).toLocaleDateString() : "";
  let tomorrowDateStr = lastUpdated ? new Date(Date.parse(lastUpdated) + 24*60*60*1000).toLocaleDateString() : "";
  return (
    <div className="roster-data" style={{ marginBottom: 40 }}>
      <h2 className="venue-title">{title}</h2>
      <div className="day-toggle" style={{ marginBottom: 16, marginTop: 12 }}>
        <button
          onClick={() => setTab("today")}
          className={`btn btn-sm ${
            tab === "today" ? "btn-primary" : "btn-outline-primary"
          }`}
          style={{ marginRight: 8 }}
        >
          Today's Roster{dateStr && ` (${dateStr})`}
        </button>
        <button
          onClick={() => setTab("tomorrow")}
          className={`btn btn-sm ${
            tab === "tomorrow" ? "btn-primary" : "btn-outline-primary"
          }`}
        >
          Tomorrow's Roster{tomorrowDateStr && ` (${tomorrowDateStr})`}
        </button>
      </div>
      {tab === "tomorrow" && !tomorrowBlock ? (
        <div className="alert alert-info" role="alert">
          <strong>Roster not yet available for tomorrow.</strong> Please check back later. This is usually released after 6 PM.
        </div>
      ) : tab === "tomorrow" && tomorrowBlock && tomorrowBlock.names && tomorrowBlock.names.length === 0 ? (
        <div className="alert alert-info" role="alert">
          <strong>Roster not yet available for tomorrow.</strong> Please check back later. This is usually released after 6 PM.
        </div>
      ) : (
        <RosterTable entries={tab === "today" ? (todayBlock ? todayBlock.names : []) : (tomorrowBlock ? tomorrowBlock.names : [])} profileVenue="elizabeth" />
      )}
    </div>
  );
}

function DataView({ data, loading, error }) {
  const [tab, setTab] = useState(0);
  if (loading) return <div>Loading...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!data) return <div>No data yet.</div>;
  // Get the last updated timestamp from any site
  const lastUpdated = data.no5?.timestamp || data.ginza?.timestamp || data.ginza479?.timestamp || data.nightshade?.timestamp || null;
  // `venue` is the slug used for profile lookups and venueInfo - keep it in step
  // with the table in .github/project-overview.md.
  const sites = [
    {
      label: "Marrickville",
      venue: "marrickville",
      content: <No5Roster site={data.no5} lastUpdated={lastUpdated} />,
    },
    {
      label: "Cleveland St",
      venue: "cleveland",
      content: <GinzaRoster site={data.ginza} lastUpdated={lastUpdated} />,
    },
    {
      label: "Elizabeth St",
      venue: "elizabeth",
      content: data.ginza479 ? <Ginza479Roster site={data.ginza479} lastUpdated={lastUpdated} /> : null,
    },
    {
      label: "Gladesville",
      venue: "gladesville",
      content: data.nightshade ? <NightshadeRoster site={data.nightshade} lastUpdated={lastUpdated} /> : null,
    },
  ].filter((site) => site.content);
  return (
    // No "container" here: this already renders inside App's .container, and a
    // nested container just adds another 12px of padding each side - which on a
    // 320px phone is width the roster table needs.
    <div className="mt-4">
      <ul className="nav nav-tabs venue-tabs mb-3">
        {sites.map((site, idx) => (
          <li className="nav-item" key={site.label}>
            <button
              className={`nav-link${tab === idx ? " active" : ""}`}
              onClick={() => setTab(idx)}
            >
              <span className="tab-bulb" aria-hidden="true" />
              {site.label}
            </button>
          </li>
        ))}
      </ul>
      <VenueInfo venue={sites[tab].venue} />
      <div>{sites[tab].content}</div>
    </div>
  );
}

export default DataView;
